import { TreeGridTranslations } from "../hooks/use-translations"
import { makeCfgBase } from "../base/layout/make-cfg-base"
import { ToolbarBuilder } from "../base/layout/make-toolbar"
import { escapeHtml, makeEnumString } from "../utils"
import { makeColumns } from "./columns"
import { ReportedHoursGroupBy, ReportedHoursVisibleColumnName } from "./types"
import { getReportedHoursGroupByOptions } from "./grouping"
import type { ReportedHoursColumnLabels } from "./columns"

export const TOTAL_HOURS_ROW_ID = "totalHoursRow"

export const makeLayout = (props: MakeLayoutProps) => {
  const Cfg = {
    ...makeCfgBase({ id: props.id, cfgId: props.cfgId }),
    Group: mapGroupByToColumn(props.groupBy),
    GroupMain: "projectOrTask",
    SaveAttrs: "Toolbar,SelectGroupBy",
    Sort: "date",
    Version: props.supportsWorkflowStatus ? 3 : 4,
  }
  const Cols = makeColumns({
    columnOrder: props.columnOrder,
    visibleColumns: props.visibleColumns,
    labels: props.labels,
  })
  const Head = makeHead(props)
  const Header = makeHeader({ columns: Cols, titleRowLabel: props.labels.titleRow })
  const Lang = makeLang(props)
  const Toolbar = makeToolbar(props)
  const Actions = makeActions()
  const Solid = [{ id: "NoData", Html: props.noDataText }]

  return {
    Cfg,
    Cols,
    Head,
    Header,
    Lang,
    Toolbar,
    Actions,
    Solid,
    Def: makeDef(),
    Panel: {
      Visible: 0,
    },
  }
}

const makeDef = () => {
  return [
    {
      Name: "Run",
      Def: "Group",
      Calculated: "1",
      Expanded: "1",
      Height: "48",
      projectOrTaskClass: "group-header",
    },
  ]
}

const makeHead = ({ dateFormat, gridInfo, showFilters, labels }: MakeHeadProps) => {
  const pageHeaderRow = {
    id: "headerRow",
    Name: labels.headerRow,
    Space: -1,
    Visible: 0,
    PanelVisible: 0,
    CanExport: 0,
    CanPrint: 2,
    Cells: "Logo,GridInfo,PrintDate",
    LogoRelWidth: 1,
    LogoAligh: "Left",
    LogoVAligh: "middle",
    LogoType: "Html",
    Logo: `<img style="height: 40px;" src="/favicon.png"  alt="company logo"/>`,
    PrintDate: new Date(),
    PrintDateType: "Date",
    PrintDateFormat: dateFormat,
    PrintDateCanEdit: 0,
    GridInfo: `<span class="largeText">${escapeHtml(gridInfo)}<span>`,
    GridInfoType: "Html",
    GridInfoRelWidth: 2,
  }
  const filtersRow = {
    CanExport: 0,
    CanPrint: 0,
    id: "filterRow",
    Kind: "Filter",
    Visible: showFilters ? 1 : 0,
  }
  const totalHoursRow = {
    id: TOTAL_HOURS_ROW_ID,
    Name: labels.totalHoursRow,
    CanEdit: 0,
    projectOrTask: labels.totalReportedHours,
    projectOrTaskClass: "reported-hours-total-label",
    hoursClass: "reported-hours-total-value",
    hoursFormula: "Grids.FormatTotalReportedHours(sum('durationInDecimalHours', '4'))",
  }

  return [pageHeaderRow, filtersRow, totalHoursRow]
}

const makeHeader = ({ columns, titleRowLabel }: MakeHeaderProps) => {
  const Header: Record<string, string | number> = {
    Name: titleRowLabel,
    Wrap: 1,
    dateClass: "text-right",
  }

  columns.forEach((col) => {
    if (col.Name) Header[col.Name] = col.header as string
  })

  return Header
}

const makeLang = ({ firstDayOfWeek, dateSeparator, translations }: MakeLangProps) => {
  return {
    Format: {
      FirstWeekDay: firstDayOfWeek,
      GMT: 1,
      DateSeparator: dateSeparator,
      LongDayNames: translations.longDayNames,
      ShortDayNames: translations.shortDayNames,
      Day2CharNames: translations.day2CharNames,
      Day1CharNames: translations.day1CharNames,
      LongMonthNames: translations.longMonthNames,
      LongMonthNames2: translations.longMonthNames,
      ShortMonthNames: translations.shortMonthNames,
    },
    MenuColumns: {
      Caption: translations.chooseItemsLabel,
      ColsCaption: translations.chooseColumnsLabel,
      SpaceCaption: translations.chooseToolbarsLabel,
      RowsCaption: translations.chooseRowsLabel,
    },
    MenuButtons: {
      Ok: translations.okButtonLabel,
      Cancel: translations.cancelButtonLabel,
      HideAll: translations.hideAllButtonLabel,
      All: translations.allOnButtonLabel,
      Clear: translations.clear,
    },
    MenuPrint: {
      Caption: translations.printOptionsLabel,
      PrintOptions: translations.printOptionsLabel,
      PrintRows: translations.printMaxRowsPerPageLabel,
      PrintPageRoot: translations.printStartRowLabel,
      PrintPageSize: translations.printPageSizeLabel,
      PrintPageOptions: translations.printBrowserPrintInfoLabel,
      PrintPageOrientation: translations.printPageOrientationLabel,
      PrintPageOrientations: `${translations.portraitLabel},${translations.landscapeLabel}`,
      PrintMarginWidth: translations.printMarginWidthLabel,
      PrintMarginHeight: translations.printMarginHeightLabel,
      PDFFitPage: translations.printFitToPageLabel,
      PDFFitPages: `${translations.noneLabel},${translations.widthLabel},${translations.heightLabel},${translations.singlePageLabel}`,
      PrintDPI: translations.printDPIRatioLabel,
      PrintWidth: translations.printPageWidthLabel,
      PrintHeight: translations.printPageHeightLabel,
      PrintResults: translations.printSizeInfo,
    },
    MenuExport: {
      Caption: translations.exportOptionsLabel,
      ExportOptions: translations.exportOptionsLabel,
      ExportName: translations.exportFileNameLabel,
      ExportFormat: translations.exportFileFormatLabel,
      ExportExpanded: translations.exportAllRowsExpandedLabel,
      ExportOutline: translations.exportTreeInExcelLabel,
      ExportIndent: translations.exportIndentTreeInMainColumnLabel,
      ExportFixedRows: translations.exportFreezeHeadRowsLabel,
      ExportFixedCols: translations.exportFreezeLeftColumnsLabel,
      ExportEmpty: translations.exportEmptyCellsLabel,
    },
    MenuFilter: {
      F0: translations.filterOffLabel,
      F1: translations.filterEqualLabel,
      F2: translations.filterNotEqualLabel,
      F3: translations.filterLessThanLabel,
      F4: translations.filterLessThanOrEqualLabel,
      F5: translations.filterGreaterThanLabel,
      F6: translations.filterGreaterThanOrEqualLabel,
      F7: translations.filterBeginsWithLabel,
      F8: translations.filterDoesnotBeginWithLabel,
      F9: translations.filterEndsWithLabel,
      F10: translations.filterDoesnotEndWithLabel,
      F11: translations.filterContainsLabel,
      F12: translations.filterDoesnotContainLabel,
    },
  }
}

const makeToolbar = ({ groupBy, showFilters, translations, columnOrder, labels }: MakeToolbarProps) => {
  return new ToolbarBuilder()
    .addCustomToolbarConfig({
      Cells10Search: "Search",
      Scales: 0,
      ScalesLabel: "",
    })
    .addToolbarSearch(translations)
    .addToolbarFilter(showFilters)
    .addToolbarTips(translations)
    .addCustomToolbarConfig({
      Cells90Right: "SelectGroupBy",
      SelectGroupByType: "Enum",
      SelectGroupByEnum: makeEnumString(
        getReportedHoursGroupByOptions({ availableColumns: columnOrder }).map((option) => labels.groupBy[option])
      ),
      SelectGroupBy: labels.groupBy[groupBy],
      SelectGroupByWidth: 200,
      SelectGroupByOnChange: "window.Grids.OnSelectGroupByChange(Grid, Value)",
      Scales: 0,
      ScalesLabel: "",
    })
    .build()
}

const makeActions = () => {
  return {
    OnIns: "",
    OnDel: "",
  }
}

type MakeLayoutProps = {
  id: string
  cfgId: string
  dateFormat: string
  dateSeparator: string
  firstDayOfWeek: DayOfWeek
  gridInfo: string
  groupBy: ReportedHoursGroupBy
  noDataText: string
  showFilters: boolean
  translations: TreeGridTranslations
  columnOrder: ReportedHoursVisibleColumnName[]
  visibleColumns: ReportedHoursVisibleColumnName[]
  supportsWorkflowStatus: boolean
  labels: ReportedHoursLayoutLabels
}

type MakeHeadProps = {
  dateFormat: string
  gridInfo: string
  showFilters: boolean
  labels: ReportedHoursLayoutLabels
}

type MakeHeaderProps = {
  columns: { header?: string; Name?: string }[]
  titleRowLabel: string
}

type MakeLangProps = {
  firstDayOfWeek: DayOfWeek
  dateSeparator: string
  translations: TreeGridTranslations
}

type MakeToolbarProps = {
  groupBy: ReportedHoursGroupBy
  showFilters: boolean
  translations: TreeGridTranslations
  columnOrder: ReportedHoursVisibleColumnName[]
  labels: ReportedHoursLayoutLabels
}

type ReportedHoursLayoutLabels = ReportedHoursColumnLabels & {
  totalReportedHours: string
  headerRow: string
  titleRow: string
  totalHoursRow: string
  groupBy: Record<ReportedHoursGroupBy, string>
}

export const mapGroupByToColumn = (groupBy: ReportedHoursGroupBy) => {
  const groupByToColumnMap = {
    [ReportedHoursGroupBy.NO_GROUPING]: "",
    [ReportedHoursGroupBy.WORKER]: "worker",
    [ReportedHoursGroupBy.PROJECT]: "project",
    [ReportedHoursGroupBy.CUSTOMER]: "customer",
    [ReportedHoursGroupBy.STATUS]: "status",
  }
  return groupByToColumnMap[groupBy]
}
